import React, { useState } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import "./HeavyUpFormEmbed.scss";
import { HEAVY_UP_FORM_ID } from "../../pages/HeavyUp/heavyUpContent";
import { EMAIL_PATTERN, formatPhoneNumber, isValidPhone } from "./formUtils";

const FORM_ID = HEAVY_UP_FORM_ID;

const emptyFullName = { first: "", last: "" };

function SectionTitle({ children }) {
	return (
		<div className="hu-form-section__title-row">
			<h2 className="hu-form-section__title">{children}</h2>
			<span className="hu-form-section__title-line" aria-hidden="true" />
		</div>
	);
}

function HeavyUpFormEmbed({ selectedPlan }) {
	const [retailer, setRetailer] = useState({
		dealershipName: "",
		dealerCode: "",
	});
	const [dealershipContact, setDealershipContact] = useState({
		name: emptyFullName,
		email: "",
		phone: "",
	});
	const [confirmed, setConfirmed] = useState(false);
	const [errors, setErrors] = useState({
		dealershipEmail: false,
		dealershipPhone: false,
	});

	const setError = (field, hasError) =>
		setErrors((prev) => ({ ...prev, [field]: hasError }));

	const handleRetailerChange = (field) => (e) =>
		setRetailer((prev) => ({ ...prev, [field]: e.target.value }));

	const handleSubmit = (e) => {
		e.preventDefault();

		const validation = {
			dealershipEmail: !EMAIL_PATTERN.test(dealershipContact.email),
			dealershipPhone: !isValidPhone(dealershipContact.phone),
		};
		if (Object.values(validation).some(Boolean)) {
			setErrors(validation);
			return;
		}

		localStorage.setItem(
			"heavyUpEnrollment",
			JSON.stringify({
				planId: selectedPlan?.id ?? "",
				retailer: retailer.dealershipName,
			}),
		);

		const form = e.target;
		form.action = `https://submit.jotform.com/submit/${FORM_ID}`;
		form.method = "post";
		form.submit();
	};

	return (
		<div className="hu-enrollment-container">
			<div className="hu-form-wrapper">
				<form
					id={FORM_ID}
					onSubmit={handleSubmit}
					acceptCharset="utf-8"
					autoComplete="on">
					<input type="hidden" name="formID" value={FORM_ID} />
					<input
						type="hidden"
						name="q125_package"
						value={selectedPlan?.id ?? ""}
					/>
					<input
						type="hidden"
						className="simple_spc"
						name="simple_spc"
						value={`${FORM_ID}-${FORM_ID}`}
					/>

					<div className="hu-form-section">
						<SectionTitle>Dealership</SectionTitle>

						<label className="hu-field">
							<span className="hu-field__label">
								Dealer Code *
							</span>
							<input
								type="text"
								name="q104_dealerCode"
								value={retailer.dealerCode}
								onChange={handleRetailerChange("dealerCode")}
								required
							/>
						</label>

						<label className="hu-field">
							<span className="hu-field__label">
								Dealership Name *
							</span>
							<input
								type="text"
								name="q18_dealershipName"
								value={retailer.dealershipName}
								onChange={handleRetailerChange(
									"dealershipName",
								)}
								required
							/>
						</label>
					</div>

					<div className="hu-form-section">
						<SectionTitle>Dealership Contact</SectionTitle>

						<div className="hu-form-row">
							<label className="hu-field">
								<span className="hu-field__label">
									First Name *
								</span>
								<input
									type="text"
									name="q14_dealershipContact[first]"
									value={dealershipContact.name.first}
									onChange={(e) =>
										setDealershipContact((prev) => ({
											...prev,
											name: {
												...prev.name,
												first: e.target.value,
											},
										}))
									}
									required
								/>
							</label>
							<label className="hu-field">
								<span className="hu-field__label">
									Last Name *
								</span>
								<input
									type="text"
									name="q14_dealershipContact[last]"
									value={dealershipContact.name.last}
									onChange={(e) =>
										setDealershipContact((prev) => ({
											...prev,
											name: {
												...prev.name,
												last: e.target.value,
											},
										}))
									}
									required
								/>
							</label>
						</div>

						<label
							className={`hu-field${errors.dealershipEmail ? " hu-field--error" : ""}`}>
							<span className="hu-field__label">
								E-mail Address *
							</span>
							<input
								type="email"
								name="q19_emailAddress"
								autoComplete="email"
								value={dealershipContact.email}
								onChange={(e) => {
									setDealershipContact((prev) => ({
										...prev,
										email: e.target.value,
									}));
									setError("dealershipEmail", false);
								}}
								onBlur={(e) =>
									setError(
										"dealershipEmail",
										e.target.value !== "" &&
											!EMAIL_PATTERN.test(
												e.target.value,
											),
									)
								}
								required
							/>
							{errors.dealershipEmail && (
								<span className="hu-field__error">
									Enter a valid email address.
								</span>
							)}
						</label>

						<label
							className={`hu-field${errors.dealershipPhone ? " hu-field--error" : ""}`}>
							<span className="hu-field__label">
								Phone Number *
							</span>
							<input
								type="tel"
								name="q20_phoneNumber[full]"
								placeholder="(000) 000-0000"
								inputMode="tel"
								autoComplete="tel"
								maxLength={14}
								value={dealershipContact.phone}
								onChange={(e) => {
									setDealershipContact((prev) => ({
										...prev,
										phone: formatPhoneNumber(
											e.target.value,
										),
									}));
									setError("dealershipPhone", false);
								}}
								onBlur={(e) =>
									setError(
										"dealershipPhone",
										e.target.value !== "" &&
											!isValidPhone(e.target.value),
									)
								}
								required
							/>
							{errors.dealershipPhone && (
								<span className="hu-field__error">
									Enter a complete 10-digit phone number.
								</span>
							)}
						</label>
					</div>

					<label className="hu-form-confirm">
						<input
							type="checkbox"
							name="q124_iConfirm"
							value="Yes"
							checked={confirmed}
							onChange={(e) => setConfirmed(e.target.checked)}
							required
						/>
						<span>
							I confirm that I am authorized to enroll this Land
							Rover retailer in the selected Heavy Up program.
						</span>
					</label>

					<div className="hu-form-footer">
						<Link to="/heavy-up-enrollment" className="hu-form-back">
							← Back to Program
						</Link>
						<p className="hu-form-required-note">
							* Required fields
						</p>
					</div>
				</form>
			</div>

			<div className="hu-summary">
				<p className="hu-summary__header">Your Program</p>
				<div className="hu-summary__item">
					<span className="hu-summary__item-label">
						{selectedPlan?.title ?? "Select a plan"}
					</span>
					<span className="hu-summary__item-price">
						{selectedPlan?.price ?? "—"}
					</span>
				</div>
				<div className="hu-summary__total">
					<div>
						<p className="hu-summary__total-label">Total</p>
						<p className="hu-summary__total-sublabel">per month</p>
					</div>
					<p className="hu-summary__total-value">
						{selectedPlan?.price ?? "—"}
					</p>
				</div>
				<button
					type="submit"
					form={FORM_ID}
					className="hu-summary__submit">
					Submit Enrollment →
				</button>
			</div>
		</div>
	);
}

HeavyUpFormEmbed.propTypes = {
	selectedPlan: PropTypes.shape({
		id: PropTypes.string,
		title: PropTypes.string,
		price: PropTypes.string,
	}),
};

export default HeavyUpFormEmbed;
